import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteError,
} from "@remix-run/react";
import { useEffect } from "react";

import isotipoImg from "./assets/academia-isotipo-256.webp";
// El primer poster del hero (`HERO_MEDIA[0]` en LaunchHero) — es el elemento LCP de la
// home en mobile. Antes acá se precargaba `administrativa.png`, que es el poster del
// SEGUNDO clip: gastábamos la prioridad alta en una imagen que no se ve hasta los 7 s y
// dejábamos la que sí se ve compitiendo con el resto.
import heroPosterUrl from "./assets/illustrations/pyme.webp";
import { TrackingManager } from "./components/consent/TrackingManager";
import { GlobalNavigationLoader } from "./components/GlobalNavigationLoader";
import { MarkdownAlternateTags } from "./components/MarkdownAlternateTags";
import { SiteVerificationMeta } from "./components/SiteVerificationMeta";
import { Toaster } from "./components/ui/toaster";
import { useNavigationTracker } from "./hooks/useNavigationTracker";
import "./lib/dateLocale";
import css from "./index.css?url";
import { reportError } from "./lib/monitoring/monitoring";
import { VisitBeacon } from "./lib/visits/VisitBeacon";
import { VisitRenderScript } from "./lib/visits/VisitRenderScript";
import CustomErrorBoundary from "./modules/common/ErrorBoundary/ErrorBoundary";
import { Providers } from "./modules/Global/Providers";
import { EXPERIENCE_INLINE_SNIPPET } from "./modules/Landing/lib/experienceLevel";
import { buildMeta } from "./utils/meta-helpers";

import type { LinksFunction, MetaFunction } from "@remix-run/node";

/** Fallback para rutas sin `meta` propio. Las hijas lo reemplazan por completo. */
export const meta: MetaFunction = () =>
  buildMeta({
    title: "AcademIA — Cursos de IA en español",
    description:
      "Aprendé IA en español, explicada para que la entienda cualquiera. Empezá hoy, gratis.",
  });

/**
 * Las fuentes se piden acá y NO con un `@import` adentro de `index.css`.
 *
 * Con el `@import`, el navegador no puede ni enterarse de esta URL hasta haber bajado y
 * parseado `index.css`, que ya es render-blocking: la cadena era HTML → index.css → CSS
 * de Google → woff2, con DNS + TLS contra dos orígenes nuevos ANTES del primer pixel.
 * Medido en un mobile de gama baja con Slow 4G eso valía 4,6 s de FCP.
 *
 * Como `<link>` en el HTML se descubre en el primer parseo y viaja en paralelo con
 * `index.css`, y los dos `preconnect` adelantan el handshake de gstatic.
 *
 * Las cuatro familias se mantienen tal cual: DM Sans (cuerpo), Fraunces (display), Caveat
 * (`.landing-hand`) y JetBrains Mono (que la home no usa, pero sí Mi Aula y los gráficos —
 * sacarla de acá les cambiaría la tipografía, y eso está fuera de alcance).
 */
const GOOGLE_FONTS_HREF =
  "https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300..900;1,9..144,300..900&family=DM+Sans:ital,opsz,wght@0,9..40,300..700;1,9..40,300..700&family=JetBrains+Mono:wght@500;600;700&family=Caveat:wght@500;600&display=swap";

export const links: LinksFunction = () => {
  return [
    { rel: "preconnect", href: "https://fonts.googleapis.com" },
    { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" as const },
    { rel: "icon", href: "/favicon.svg", type: "image/svg+xml" },
    { rel: "icon", href: "/favicon.ico", sizes: "32x32" },
    { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
    { rel: "manifest", href: "/site.webmanifest" },
    // El isotipo del splash no va acá: React ya emite su `<link rel="preload">` por el
    // `<img>` del splash, y a mano salía duplicado. Antes acá se precargaba el logo viejo
    // (`academia-logo-opt.png`), que no se mostraba en ningún lado.
    { rel: "preload", as: "image", href: heroPosterUrl },
    { rel: "stylesheet", href: GOOGLE_FONTS_HREF },
    { rel: "stylesheet", href: css },
  ];
};

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="es-AR" suppressHydrationWarning>
      <head suppressHydrationWarning>
        <meta charSet="utf-8" />
        {/* `interactive-widget=resizes-content`: con el teclado abierto, Android achica el
            viewport de LAYOUT en vez de sólo el visual. Sin esto el reproductor —que es
            `fixed inset-0`— no se enteraba de que el teclado estaba tapando el composer del
            chat. iOS lo ignora y se resuelve por JS (`useKeyboardInset`). */}
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, interactive-widget=resizes-content"
        />
        <meta name="theme-color" content="#C0392B" />
        <script
          dangerouslySetInnerHTML={{
            __html: `
              (function () {
                try {
                  var stored = JSON.parse(localStorage.getItem('theme-storage') || '{}');
                  var preference = stored && stored.state && stored.state.theme || 'system';
                  var dark = preference === 'dark' ||
                    (preference === 'system' && matchMedia('(prefers-color-scheme: dark)').matches);
                  document.documentElement.classList.toggle('dark', dark);
                  document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
                } catch (_) {}
              })();
            `,
          }}
        />
        {/* Nivel de experiencia (`full` / `reduced`), resuelto ANTES del primer pintado —
            mismo mecanismo que la clase `dark` de arriba y por el mismo motivo: quien pidió
            "menos movimiento" en su sistema operativo no puede llegar a ver un solo frame
            de movimiento. Acá sólo se evalúan los overrides duros; el puntaje de capacidad
            y las sondas de runtime corren después de hidratar.
            Criterio completo en `modules/Landing/lib/experienceLevel.ts`. */}
        <script dangerouslySetInnerHTML={{ __html: EXPERIENCE_INLINE_SNIPPET }} />
        {/* Los OG/twitter salen de `meta` (abajo) y de cada ruta: acá estaban duplicados
            y con la URL de la imagen hardcodeada a prod, que no resuelve. */}
        <Meta />
        <Links />
        <MarkdownAlternateTags siteUrl={import.meta.env.VITE_APP_URL || ""} />
        {/* Verificación de dominio de Search Console, Meta y Bing: sólo si hay token cargado
            en /admin/tracking. No hay ningún script de medición en el <head>: los píxeles los
            carga `TrackingManager` después de hidratar, sin esperar consentimiento salvo que el
            aviso de cookies esté prendido o la persona haya elegido no ser medida (/privacy). */}
        <SiteVerificationMeta />
      </head>
      <body>
        {/* ═══ SPLASH SCREEN — visible from first byte, dismissed after load ═══ */}
        <div
          id="academia-splash"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: "16px",
            background: "rgb(var(--landing-cream, 245 240 228))",
            transition: "opacity 0.4s ease-out",
          }}
        >
          <img
            src={isotipoImg}
            alt=""
            width={72}
            height={72}
            style={{
              width: "72px",
              height: "72px",
              objectFit: "contain",
              animation: "splash-breathe 2s ease-in-out infinite",
            }}
          />
          <svg
            width="220"
            height="44"
            viewBox="0 0 220 44"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ animation: "splash-breathe 2s ease-in-out infinite" }}
          >
            <text
              x="110"
              y="34"
              textAnchor="middle"
              fontFamily="Georgia, serif"
              fontSize="32"
              fontWeight="600"
              letterSpacing="-0.5"
            >
              <tspan style={{ fill: "rgb(var(--landing-text, 31 26 21))" }}>Academ</tspan>
              <tspan style={{ fill: "rgb(var(--landing-red, 192 57 43))" }}>IA</tspan>
            </text>
          </svg>
          {/* splash bar below logo */}
          <div
            style={{
              width: "40px",
              height: "2px",
              background: "rgb(var(--landing-red, 192 57 43))",
              borderRadius: "1px",
              animation: "splash-bar 1.5s ease-in-out infinite",
            }}
          />
        </div>
        <style
          dangerouslySetInnerHTML={{
            __html: `
              @keyframes splash-breathe {
                0%, 100% { opacity: 1; transform: scale(1); }
                50% { opacity: 0.7; transform: scale(1.02); }
              }
              @keyframes splash-bar {
                0%, 100% { width: 40px; opacity: 0.3; }
                50% { width: 80px; opacity: 1; }
              }
            `,
          }}
        />
        <script
          dangerouslySetInnerHTML={{
            __html: `
              // El splash tapa la página entera. Todo lo que espere acá se le suma DIRECTO
              // al LCP, porque atrás ya hay contenido servido por SSR listo para verse.
              //
              // Antes esperaba a \`document.fonts.ready\` (o sea a las cuatro familias de
              // Google Fonts) y a un PNG de 196 KB, con un tope de 3 s. En un mobile de
              // gama baja con Slow 4G eso significaba tener la home tapada esos 3 s enteros.
              //
              // Ahora espera SÓLO al isotipo —que es lo único que el propio splash dibuja—
              // y como mucho 800 ms. Las fuentes ya cargan con \`display=swap\`: el texto se
              // ve de entrada con la fuente del sistema y se reemplaza solo.
              (function() {
                var splash = document.getElementById('academia-splash');
                if (!splash) return;

                function dismissSplash() {
                  if (splash.dataset.dismissed) return;
                  splash.dataset.dismissed = '1';
                  splash.style.opacity = '0';
                  splash.style.pointerEvents = 'none';
                  setTimeout(function() { splash.style.display = 'none'; }, 400);
                }

                var logo = new Image();
                logo.onload = dismissSplash;
                logo.onerror = dismissSplash;
                logo.src = ${JSON.stringify(isotipoImg)};
                if (logo.complete) dismissSplash();

                setTimeout(dismissSplash, 800);
              })();
            `,
          }}
        />
        {children}
        {/* El playerjs de Bunny ya NO se carga acá. Vivía en el documento de TODAS las
            páginas —la home pública abría DNS + TLS contra un quinto origen para un
            reproductor que sólo existe dentro de una clase—. Ahora lo pide `VideoPlayer`
            cuando se monta (`components/VideoPlayer/loadPlayerJs.ts`). */}
        <ScrollRestoration />
        {/* Registro propio de visitas: avisa que este documento se mostró (id en el HTML, sólo
            si server.js lo registró). Ver `lib/visits/visit-render-snippet.ts`. */}
        <VisitRenderScript />
        <Scripts />
        <Toaster />
      </body>
    </html>
  );
}

export default function Root() {
  useNavigationTracker();

  return (
    <Providers>
      <GlobalNavigationLoader />
      <Outlet />
      <TrackingManager />
      <VisitBeacon />
    </Providers>
  );
}

export function HydrateFallback() {
  return <div />;
}
export function ErrorBoundary() {
  const error = useRouteError();

  // Los errores que ataja Remix no llegan al handler global de Sentry: se reportan acá (no-op
  // sin DSN). Los 404/401 de ruta no son errores del front.
  useEffect(() => {
    if (!isRouteErrorResponse(error)) reportError(error);
  }, [error]);

  if (isRouteErrorResponse(error)) {
    return <CustomErrorBoundary stack={error.data} message={error.statusText} />;
  } else if (error instanceof Error) {
    return <CustomErrorBoundary stack={error.stack} message={error.message} />;
  } else {
    return <CustomErrorBoundary />;
  }
}
